/* eslint-disable react-refresh/only-export-components -- context provider dan hook adalah satu API publik. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiRequest, queryString } from "../lib/api";
import { normalizeBootstrap, type Identifier, type StoreBootstrap } from "../lib/types";

const BRANCH_KEY = "sedapsayur_branch_id";

interface StoreContextValue {
  store?: StoreBootstrap;
  loading: boolean;
  error?: string;
  activeBranchId?: Identifier;
  reload: () => Promise<void>;
  selectBranch: (branchId: Identifier) => Promise<void>;
}

const StoreContext = createContext<StoreContextValue | undefined>(undefined);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [store, setStore] = useState<StoreBootstrap>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const load = useCallback(async (branchId?: Identifier) => {
    setLoading(true);
    setError(undefined);
    try {
      const payload = await apiRequest<unknown>(
        `/api/public/bootstrap${queryString({ branch_id: branchId === undefined ? undefined : String(branchId) })}`,
      );
      const nextStore = normalizeBootstrap(payload);
      setStore(nextStore);
      if (nextStore.activeBranch?.id !== undefined) {
        localStorage.setItem(BRANCH_KEY, String(nextStore.activeBranch.id));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Toko belum dapat dimuat.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const savedBranch = localStorage.getItem(BRANCH_KEY) ?? undefined;
    const timer = window.setTimeout(() => void load(savedBranch), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  const selectBranch = useCallback(
    async (branchId: Identifier) => {
      await load(branchId);
    },
    [load],
  );

  const value = useMemo<StoreContextValue>(
    () => ({
      store,
      loading,
      error,
      activeBranchId: store?.activeBranch?.id,
      reload: () => load(store?.activeBranch?.id),
      selectBranch,
    }),
    [error, load, loading, selectBranch, store],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreContextValue {
  const context = useContext(StoreContext);
  if (!context) throw new Error("useStore harus digunakan di dalam StoreProvider");
  return context;
}
